import { getAllowedParentOrigins } from '../config/embedConfig';
import { isAllowedParentOrigin } from '../security/origin';
import {
  createEmbedReadyMessage,
  createLaunchErrorMessage,
  createLaunchSuccessMessage,
  parseBanyanproInboundMessage,
  type BanyanproInboundMessage,
  type TkmOutboundMessage,
} from './protocol';

export type AssertionMessageHandler = (message: BanyanproInboundMessage) => void;

/**
 * Post TKM_EMBED_READY to allowed parent origins (never includes JWT).
 */
export function postEmbedReadyToParent(): void {
  const msg = createEmbedReadyMessage();
  const targets = getAllowedParentOrigins();
  if (targets.length === 0) {
    return;
  }
  for (const targetOrigin of targets) {
    try {
      window.parent.postMessage(msg, targetOrigin);
    } catch {
      /* ignore cross-origin post failures */
    }
  }
}

export function postLaunchSuccessToParent(path?: string): void {
  postToAllowedParents(createLaunchSuccessMessage(path));
}

export function postLaunchErrorToParent(code: string, message: string): void {
  postToAllowedParents(createLaunchErrorMessage(code, message));
}

function postToAllowedParents(message: TkmOutboundMessage): void {
  const targets = getAllowedParentOrigins();
  for (const targetOrigin of targets) {
    try {
      window.parent.postMessage(message, targetOrigin);
    } catch {
      /* ignore */
    }
  }
}

/**
 * Listen for BANYANPRO_ASSERTION from parent; strict origin verification.
 */
export function attachParentAssertionListener(
  onAssertion: AssertionMessageHandler,
  _onDeniedOrigin?: (origin: string) => void,
  onMalformed?: () => void
): () => void {
  const handler = (event: MessageEvent) => {
    if (event.source !== window.parent) {
      return;
    }
    const origin = event.origin;
    if (!isAllowedParentOrigin(origin)) {
      // Silently ignore — do not settle launch (browser extensions / noise).
      return;
    }
    const parsed = parseBanyanproInboundMessage(event.data);
    if (!parsed) {
      onMalformed?.();
      return;
    }
    onAssertion(parsed);
  };

  window.addEventListener('message', handler);
  return () => window.removeEventListener('message', handler);
}
